/**
 * AEO Impact Demo Page
 *
 * Interactive visualization showing the impact of AEO elements on page metrics
 * and semantic relevance to user questions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, RefreshCw, Sparkles, ExternalLink, Search, Check, ChevronsUpDown, BarChart3, Target, Trophy } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useSite } from '@/contexts/SiteContext';
import { useAEODemo } from '@/hooks/useAEODemo';
import { AEOElementsLibrary } from '@/components/aeo-demo/AEOElementsLibrary';
import { DualMetricsPanel } from '@/components/aeo-demo/DualMetricsPanel';
import { SemanticDistanceViz } from '@/components/aeo-demo/SemanticDistanceViz';
import { PageRaceViz } from '@/components/aeo-demo/PageRaceViz';
import { getCuratedProducts, CuratedProduct } from '@/services/aeoDemoService';
import { authFetch } from '@/utils/auth-fetch';

interface ProductOption {
  id: string;
  name: string;
  category: string;
}

export default function AEOImpactDemo() {
  const [searchParams] = useSearchParams();
  const productIdFromUrl = searchParams.get('productId');
  const { selectedSite } = useSite();

  // Product selection state
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(productIdFromUrl);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [curatedProducts, setCuratedProducts] = useState<CuratedProduct[]>([]);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Demo hook
  const {
    session,
    isInitializing,
    initError,
    elements,
    selectedElementIds,
    baselineMetrics,
    enhancedMetrics,
    competitorMetrics,
    faqQuestions,
    isCalculating,
    calculationTime,
    activePreset,
    initSession,
    toggleElement,
    selectPreset,
    clearSelections,
    forceRecalculate,
  } = useAEODemo();

  // Load curated products on mount
  useEffect(() => {
    async function loadCurated() {
      try {
        const products = await getCuratedProducts();
        setCuratedProducts(products);
      } catch (error) {
        console.error('Failed to load curated products:', error);
      }
    }
    loadCurated();
  }, []);

  // Load products with AEO content for the selected site
  useEffect(() => {
    async function loadProducts() {
      if (!selectedSite?.id) return;

      setLoadingProducts(true);
      try {
        // Fetch products that actually have AEO content
        const response = await authFetch(
          `/api/aeo-demo/products/${selectedSite.id}?limit=200`
        );
        if (response.ok) {
          const data = await response.json();
          setProductOptions(
            (data.products || []).map((p: any) => ({
              id: p.id,
              name: p.product_name || p.title || p.name || 'Unnamed Product',
              category: p.category_path || p.category || '',
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load products:', error);
      }
      setLoadingProducts(false);
    }
    loadProducts();
  }, [selectedSite?.id]);

  // Initialize session when product selected
  useEffect(() => {
    if (selectedProductId && !session) {
      initSession(selectedProductId, 'product');
    }
  }, [selectedProductId, session, initSession]);

  // Auto-select from URL
  useEffect(() => {
    if (productIdFromUrl) {
      setSelectedProductId(productIdFromUrl);
    }
  }, [productIdFromUrl]);

  const handleProductSelect = (productId: string) => {
    setSelectedProductId(productId);
    if (session && session.product.id !== productId) {
      initSession(productId, 'product');
    }
  };

  const handleCuratedSelect = (productId: string) => {
    setSelectedProductId(productId);
    initSession(productId, 'standalone_demo');
  };

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return productOptions;
    const query = searchQuery.toLowerCase();
    return productOptions.filter(
      p => p.name.toLowerCase().includes(query) ||
           p.category.toLowerCase().includes(query)
    );
  }, [productOptions, searchQuery]);

  // Get selected product info for display
  const selectedProduct = useMemo(() => {
    return productOptions.find(p => p.id === selectedProductId);
  }, [productOptions, selectedProductId]);

  // Render loading state
  if (isInitializing) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex flex-col items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Initializing demo session...</p>
          <p className="text-xs text-muted-foreground mt-2">
            Generating FAQ questions and computing baseline metrics
          </p>
        </div>
      </div>
    );
  }

  // Render error state
  if (initError) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to initialize demo: {initError}
          </AlertDescription>
        </Alert>
        <Button
          className="mt-4"
          onClick={() => selectedProductId && initSession(selectedProductId, 'product')}
        >
          Try Again
        </Button>
      </div>
    );
  }

  // Render product selection if no session
  if (!session) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">AEO Impact Demo</h1>
            <p className="text-muted-foreground">
              Visualize how AEO elements improve page metrics and semantic relevance
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Curated Demo Products */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                Demo Products
              </CardTitle>
              <CardDescription>
                Pre-selected products with complete AEO content for demonstration
              </CardDescription>
            </CardHeader>
            <CardContent>
              {curatedProducts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No curated demo products configured yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {curatedProducts.map(product => (
                    <Button
                      key={product.id}
                      variant="outline"
                      className="w-full justify-start h-auto py-3"
                      onClick={() => handleCuratedSelect(product.id)}
                    >
                      <div className="text-left">
                        <div className="font-medium">{product.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {product.category} • {product.siteName}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Site Products */}
          <Card>
            <CardHeader>
              <CardTitle>Your Products</CardTitle>
              <CardDescription>
                Select a product from {selectedSite?.name || 'your site'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedSite ? (
                <p className="text-sm text-muted-foreground">
                  Please select a site from the header to see products.
                </p>
              ) : loadingProducts ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading products...
                </div>
              ) : productOptions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No products with AEO content found. Run the AEO pipeline first.
                </p>
              ) : (
                <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboboxOpen}
                      className="w-full justify-between h-auto min-h-10 py-2"
                    >
                      {selectedProduct ? (
                        <div className="text-left truncate flex-1 mr-2">
                          <div className="font-medium truncate">{selectedProduct.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {selectedProduct.category}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Search and select a product...</span>
                      )}
                      <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search products..."
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                      />
                      <CommandList className="max-h-[300px]">
                        <CommandEmpty>No products found.</CommandEmpty>
                        <CommandGroup>
                          {filteredProducts.slice(0, 50).map(product => (
                            <CommandItem
                              key={product.id}
                              value={product.id}
                              onSelect={() => {
                                handleProductSelect(product.id);
                                setComboboxOpen(false);
                                setSearchQuery('');
                              }}
                              className="cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4 shrink-0",
                                  selectedProductId === product.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{product.name}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {product.category}
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                          {filteredProducts.length > 50 && (
                            <div className="py-2 px-3 text-xs text-muted-foreground text-center">
                              Showing 50 of {filteredProducts.length} products. Type to search more...
                            </div>
                          )}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main demo view
  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AEO Impact Demo</h1>
          <p className="text-muted-foreground flex items-center gap-2">
            {session.product.name}
            <Badge variant="outline">{session.product.category}</Badge>
            {session.product.originalPageUrl && (
              <a
                href={session.product.originalPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                View Original
              </a>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {calculationTime > 0 && (
            <span className="text-xs text-muted-foreground">
              Last calc: {calculationTime}ms
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={forceRecalculate}
            disabled={isCalculating || selectedElementIds.length === 0}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isCalculating ? 'animate-spin' : ''}`} />
            Recalculate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSelectedProductId(null);
              clearSelections();
            }}
          >
            Change Product
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Elements Library (Left Sidebar) */}
        <div className="lg:col-span-1">
          <AEOElementsLibrary
            elements={elements}
            selectedIds={selectedElementIds}
            onToggle={toggleElement}
            onPresetSelect={selectPreset}
            onClear={clearSelections}
            activePreset={activePreset}
            disabled={isCalculating}
          />
        </div>

        {/* Visualizations (Main Area) */}
        <div className="lg:col-span-3">
          <Tabs defaultValue="race" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="race" className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Page Race
              </TabsTrigger>
              <TabsTrigger value="metrics" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Metrics Comparison
              </TabsTrigger>
              <TabsTrigger value="semantic" className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Semantic Relevance
              </TabsTrigger>
            </TabsList>

            <TabsContent value="race">
              <PageRaceViz
                baseline={baselineMetrics}
                enhanced={enhancedMetrics}
                competitor={competitorMetrics}
                isCalculating={isCalculating}
              />
            </TabsContent>

            <TabsContent value="metrics">
              <DualMetricsPanel
                baseline={baselineMetrics}
                enhanced={enhancedMetrics}
                competitor={competitorMetrics}
                isCalculating={isCalculating}
              />
            </TabsContent>

            <TabsContent value="semantic">
              <SemanticDistanceViz
                questions={faqQuestions}
                hasEnhancement={selectedElementIds.length > 0}
                fullSize
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
